/**
 * Cart Utilities
 * Centralized cart operations for consistent behavior across the theme
 * 
 * USAGE EXAMPLES:
 * 
 * 1. Add product to cart:
 *    await CartUtils.addToCart(variantId, quantity);
 * 
 * 2. Add product with properties:
 *    await CartUtils.addToCart(variantId, 1, { 
 *      'Gift message': 'Happy Birthday!' 
 *    });
 * 
 * 3. Update quantity:
 *    await CartUtils.updateQuantity(lineIndex, newQuantity);
 * 
 * 4. Get current cart:
 *    const cart = await CartUtils.getCart();
 * 
 * 5. Refresh cart drawer:
 *    CartUtils.refreshCartDrawer();
 * 
 * 6. Open/Close cart drawer:
 *    CartUtils.openCartDrawer();
 *    CartUtils.closeCartDrawer();
 * 
 * All add/update operations automatically:
 * - Update the cart drawer with new content
 * - Trigger cart update events
 * - Handle errors gracefully
 */

class CartUtils {
  /**
   * Add item(s) to cart and update cart drawer
   * @param {number|string} variantId - The variant ID to add
   * @param {number} quantity - Quantity to add (default: 1)
   * @param {Object} properties - Optional line item properties
   * @returns {Promise} - Promise that resolves with cart data
   */
  static async addToCart(variantId, quantity = 1, properties = {}) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      // Add properties if any
      Object.keys(properties).forEach(key => {
        formData.append(`properties[${key}]`, properties[key]);
      });
      
      // Get cart drawer to render sections
      const cartDrawer = document.querySelector('cart-drawer');
      
      if (cartDrawer) {
        const sectionsToRender = cartDrawer.getSectionsToRender().map((section) => section.id);
        formData.append('sections', sectionsToRender);
        formData.append('sections_url', window.location.pathname);
      }
      
      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };
      
      // Use .js endpoint for JSON response instead of HTML redirect
      const cartAddUrl = window.routes.cart_add_url ? `${window.routes.cart_add_url}.js` : '/cart/add.js';
      const response = await fetch(cartAddUrl, config);
      
      if (!response.ok) {
        let errorMessage = 'Failed to add to cart';
        try {
          const errorData = await response.json();
          errorMessage = errorData.description || errorData.message || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      // Parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (error) {
        console.error('Failed to parse cart response as JSON:', error);
        throw new Error('Invalid response from server. Expected JSON but got something else.');
      }
      
      // Update cart drawer if it exists
      if (cartDrawer) {
        // Fetch the cart-drawer section specifically (not main-cart-items!)
        fetch('/cart?section_id=cart-drawer')
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const cartDrawerSection = html.querySelector('#CartDrawer');
            
            if (cartDrawerSection) {
              // Update the drawer content
              const currentDrawer = document.querySelector('#CartDrawer');
              if (currentDrawer) {
                currentDrawer.innerHTML = cartDrawerSection.innerHTML;
              }
            }
            
            // Open the cart drawer
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
            
            // Update cart icon bubble count
            fetch('/cart?section_id=cart-icon-bubble')
              .then(response => response.text())
              .then(responseText => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                const iconBubble = html.querySelector('#cart-icon-bubble');
                const currentBubble = document.querySelector('#cart-icon-bubble');
                if (iconBubble && currentBubble) {
                  currentBubble.innerHTML = iconBubble.innerHTML;
                }
              })
              .catch(e => console.error('Error updating cart icon:', e));
            
            // Reinitialize cart recommendations after drawer updates
            setTimeout(() => {
              if (typeof window.initCartRecommendations === 'function') {
                window.initCartRecommendations();
              }
            }, 250);
          })
          .catch(e => {
            console.error('Error updating cart drawer:', e);
          });
      }
      
      // Publish cart update event for other components
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-utils'});
      }
      
      // Trigger custom events for legacy support
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      return data;
      
    } catch (error) {
      console.error('Cart add error:', error);
      throw error;
    }
  }
  
  /**
   * Update item quantity in cart
   * @param {number} line - Line item index (1-based)
   * @param {number} quantity - New quantity
   * @returns {Promise} - Promise that resolves with cart data
   */
  static async updateQuantity(line, quantity) {
    try {
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSections(),
        sections_url: window.location.pathname
      });
      
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body
      };
      
      const response = await fetch(`${window.routes.cart_change_url}`, config);
      
      if (!response.ok) {
        throw new Error('Failed to update cart');
      }
      
      const data = await response.json();
      
      // Trigger refresh (this will also reinitialize recommendations)
      this.refreshCartDrawer();
      
      return data;
      
    } catch (error) {
      console.error('Cart update error:', error);
      throw error;
    }
  }
  
  /**
   * Get current cart state
   * @returns {Promise} - Promise that resolves with cart data
   */
  static async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return await response.json();
    } catch (error) {
      console.error('Cart fetch error:', error);
      throw error;
    }
  }
  
  /**
   * Refresh cart drawer content
   */
  static refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    
    if (cartDrawerItems) {
      cartDrawerItems.onCartUpdate();
    }
    
    // Reinitialize cart recommendations after refresh
    setTimeout(() => {
      if (typeof window.initCartRecommendations === 'function') {
        window.initCartRecommendations();
      }
    }, 250);
    
    // Publish cart update event
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-utils'});
    }
    
    // Trigger custom events
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
  
  /**
   * Get sections to render for cart updates
   * @returns {Array} - Array of section IDs
   */
  static getSections() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }
  
  /**
   * Open cart drawer
   */
  static openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.open();
    }
  }
  
  /**
   * Close cart drawer
   */
  static closeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.close();
    }
  }
}

// Make CartUtils available globally
window.CartUtils = CartUtils;

